import React, { useCallback, useLayoutEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { GraduationCap, Sparkles } from "lucide-react";
import UserProfileSidebar from "components/SideMenu/UserProfileSidebar";
import { SLEEK_TOPBAR } from "pages/DesignCanvasAgentPage/components/sleek";
import WhatsNewMenu, { NotificationsMenu } from "components/WhatsNewMenu";
import { cn } from "utils/cn";
import { useAppDispatch, useAppSelector } from "store";
import { setChatbotOpen } from "store/commonSlice";
import { PATHS } from "router/config/paths";
import { AnalyticsEvent } from "utils";

const DashboardTopBar = ({ mode = "full" }: { mode?: "full" | "agent" }) => {
  const { t } = useTranslation();
  // "agent" trims the cluster to just the Ask Agent button (used on create pages).
  const isAgentOnly = mode === "agent";
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const isChatbotOpen = useAppSelector((s) => s.common.isChatbotOpen);
  const clusterRef = useRef<HTMLDivElement>(null);

  const isAcademy = location.pathname === PATHS.ACADEMY_HOME || location.pathname.startsWith(`${PATHS.ACADEMY_HOME}/`);

  // Publish the bar's *measured* right-edge footprint (incl. the px-3.5 inset + a 16px gap) as a CSS var on :root.
  // The Academy / Ask Agent labels differ in width per locale ("Ask Agent" vs es "Preguntar al Agente"), so anything
  // that needs to sit clear of this bar must read this var instead of hardcoding a px offset (see CLAUDE.md Trap #13).
  // Re-measures on locale switch / font-load / resize via ResizeObserver; the var is removed whenever the bar isn't shown
  // (mobile shell renders MobileTopBar instead) so consumers fall back to their corner default. On Academy the bar still
  // renders — only its Academy button is hidden — so the var is published there too, just narrower.
  useLayoutEffect(() => {
    const el = clusterRef.current;
    const root = document.documentElement;

    if (!el) {
      root.style.removeProperty("--dashboard-topbar-clearance");

      return;
    }

    const update = () => {
      const clearance = Math.max(0, Math.round(window.innerWidth - el.getBoundingClientRect().left)) + 16;
      root.style.setProperty("--dashboard-topbar-clearance", `${clearance}px`);
    };

    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    window.addEventListener("resize", update);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", update);
      root.style.removeProperty("--dashboard-topbar-clearance");
    };
  }, [isAcademy]);

  const goToAcademy = useCallback(() => {
    AnalyticsEvent("nav_academy_click", "topbar", "click");
    navigate(PATHS.ACADEMY_HOME);
  }, [navigate]);

  const toggleChatbot = useCallback(() => {
    dispatch(setChatbotOpen(!isChatbotOpen));
  }, [dispatch, isChatbotOpen]);

  // On Academy, hide only the Academy button — linking to the page you're already on is the
  // reason this bar used to bail out entirely. Bailing out also removed the Ask Agent button,
  // and since DashboardChatbot has no launcher of its own, Academy was the one authorized
  // desktop surface where the chatbot could not be opened at all. The panel is still rendered
  // there (Layout mounts it whenever authorized), so the only thing missing was the entry point.
  return (
    <div className="sticky top-0 z-20 h-0">
      {/* Cluster is vertically centered in the 48px header band it shares with the page title + sidebar logo row. */}
      <div className="flex justify-end items-center h-12 px-3.5 pointer-events-none">
        <div ref={clusterRef} data-dashboard-topbar className="flex items-center gap-2 pointer-events-auto">
          {!isAgentOnly && !isAcademy && (
            <button
              onClick={goToAcademy}
              className={cn("flex h-9 items-center gap-1.5 rounded-full px-3.5 text-[13px] font-medium text-trueBlack transition-[filter] hover:brightness-[0.97] dark:text-white", SLEEK_TOPBAR)}
            >
              <GraduationCap className="h-[18px] w-[18px]" />
              <span>{t("nav.academy")}</span>
            </button>
          )}
          <button
            onClick={toggleChatbot}
            className={cn(
              "flex h-9 items-center gap-1.5 rounded-full px-3.5 text-[13px] font-medium text-trueBlack transition-[filter] hover:brightness-[0.97] dark:text-white",
              SLEEK_TOPBAR,
              isChatbotOpen && "brightness-[0.94]"
            )}
            title={t("chatbot.open_aria")}
          >
            <Sparkles className="h-[18px] w-[18px]" />
            <span>{t("chatbot.title")}</span>
          </button>
          {!isAgentOnly && (
            <>
              {/* What's New announcements — icon+label button next to Ask Agent (opens the announcements feed). */}
              <WhatsNewMenu />
              {/* Notifications — standalone square bell (its own feed + unread badge). */}
              <NotificationsMenu />
              {/* Far-right: the user avatar (moved here from the sidebar bottom). Dropdown carries credits, language, theme + account menu. */}
              <UserProfileSidebar variant="topbar" />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default DashboardTopBar;
